import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';
import { API_ENDPOINTS } from '../../../utils/config';

const SurveyAssignmentModal = ({ 
  isOpen, 
  onClose, 
  onSuccess, 
  property, 
  client,
  clients = [], // Add clients array as fallback
  propertySurveyStatus = {} // Add survey status tracking
}) => {
  const [surveyors, setSurveyors] = useState([]);
  const [surveyorStats, setSurveyorStats] = useState({});
  const [surveyorSearch, setSurveyorSearch] = useState('');
  const [selectedSurveyor, setSelectedSurveyor] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);
  const [error, setError] = useState('');
  const [resolvedClient, setResolvedClient] = useState(null);
  const [existingSurveyInfo, setExistingSurveyInfo] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  // Fetch surveyors and resolve client when modal opens
  useEffect(() => {
    if (isOpen) {
      checkExistingSurvey();
      resolveClient();
      fetchSurveyors();
    }
  }, [isOpen, property, client, clients, propertySurveyStatus]);

  const resolveClient = () => {
    setResolvedClient(client || null);
  };

  const getClientName = () => resolvedClient?.name || 'Not found';

  const getClientCode = () => resolvedClient?.client_code || 'Not available';

  const checkExistingSurvey = () => {
    // Check if there's existing survey information for this property
    // This would typically come from an API call, but for now we'll use the propertySurveyStatus
    // that's passed from the parent component
    if (property && propertySurveyStatus && propertySurveyStatus[property.id]) {
      const surveyInfo = propertySurveyStatus[property.id];
      setExistingSurveyInfo(surveyInfo);
    } else {
      setExistingSurveyInfo(null);
    }
  };

  const fetchSurveyors = async () => {
    setIsLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Accept': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      };

      // Fetch surveyors with workload stats
      let userRolesData = [];
      let response;

      try {
        response = await axios.get(API_ENDPOINTS.SURVEYORS_LIST, { headers });
      } catch {
        response = await axios.get(API_ENDPOINTS.USER_ROLES_LIST, { headers });
      }

      // Build surveyorStats from the response directly
      const rawSurveyors = Array.isArray(response.data)
        ? response.data
        : (response.data?.results || response.data?.data || response.data?.surveyors || []);

      const stats = {};
      rawSurveyors.forEach(s => {
        stats[s.id] = {
          total: s.total_surveys || 0,
          pending: s.pending_count || 0,
          completed: s.completed_count || 0,
          total: s.total_surveys || 0,
        };
      });
      setSurveyorStats(stats);

      userRolesData = rawSurveyors.map(surveyor => ({
        id: surveyor.id,
        user_detail: {
          id: surveyor.id,
          username: surveyor.username,
          email: surveyor.email
        },
        role_detail: {
          name: 'Surveyor',
          id: surveyor.role_id || surveyor.id
        }
      }));
      
      // Filter users with surveyor role (assuming role name contains 'surveyor' or similar)
      const surveyorUsers = userRolesData.filter(userRole => {
        const roleName = userRole?.role_detail?.name?.toLowerCase() || '';
        const isSurveyor = roleName.includes('surveyor') || roleName.includes('field') || roleName.includes('survey');
        return isSurveyor;
      });

      // Transform to options format and exclude currently assigned surveyor
      const surveyorOptions = surveyorUsers
        .filter(userRole => {
          // If there's an existing survey, exclude the currently assigned surveyor
          const isCurrentSurveyor = existingSurveyInfo && existingSurveyInfo.surveyor?.id === userRole?.user_detail?.id;
          if (isCurrentSurveyor) {
            return false;
          }
          return true;
        })
        .map(userRole => ({
          value: userRole?.user_detail?.id,
          label: `${userRole?.user_detail?.username} (${userRole?.user_detail?.email})`
        }));
      
      // If no surveyors found with role filtering, try to get all users as fallback
      if (surveyorOptions.length === 0 && userRolesData.length > 0) {
        const fallbackOptions = userRolesData
          .filter(userRole => {
            // If there's an existing survey, exclude the currently assigned surveyor
            const isCurrentSurveyor = existingSurveyInfo && existingSurveyInfo.surveyor?.id === userRole?.user_detail?.id;
            if (isCurrentSurveyor) {
              return false;
            }
            return true;
          })
          .map(userRole => ({
            value: userRole?.user_detail?.id,
            label: `${userRole?.user_detail?.username} (${userRole?.user_detail?.email}) - ${userRole?.role_detail?.name || 'No Role'}`
          }));
        
        setSurveyors(fallbackOptions);
      } else if (surveyorOptions.length === 0 && userRolesData.length === 0) {
        setSurveyors([]);
        setError('No surveyors available. Please contact your administrator.');
      } else {
        setSurveyors(surveyorOptions);
      }
    } catch (error) {
      const errorMessage = error?.response?.data?.message || 
                          error?.response?.data?.detail || 
                          error?.response?.data?.error ||
                          `Failed to load surveyors. Status: ${error?.response?.status}. Please try again.`;
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAssignSurvey = async () => {
    if (!selectedSurveyor) {
      setError('Please select a surveyor');
      return;
    }


    setIsAssigning(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      
      // Clean token - remove Bearer prefix if it exists
      const cleanToken = token && token.startsWith('Bearer ') ? token.substring(7) : token;
      
      const headers = {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        ...(cleanToken ? { Authorization: `Bearer ${cleanToken}` } : {})
      };

      // Check if token exists
      if (!cleanToken) {
        setError('Authentication required. Please log in again.');
        return;
      }

      let response;
      let successMsg;

      // Check if this is a reassign operation (existing survey)
      if (existingSurveyInfo && existingSurveyInfo.surveyId) {
        // Ensure survey ID is a valid number
        const surveyId = parseInt(existingSurveyInfo.surveyId);
        if (!surveyId || isNaN(surveyId)) {
          setError('Invalid survey ID. Please refresh and try again.');
          return;
        }
        
        const reassignBody = {
          surveyor_id: parseInt(selectedSurveyor)
        };
        
        response = await axios.put(API_ENDPOINTS.SURVEY_REASSIGN(surveyId), reassignBody, { headers });
        successMsg = response.data?.message || 'Survey reassigned successfully';
        
        // Use the detailed message from API if available
        if (response.data?.previous_assignment && response.data?.new_assignment) {
          successMsg = `Survey reassigned successfully from ${response.data.previous_assignment.surveyor_name} to ${response.data.new_assignment.surveyor_name}`;
        }
      } else {
        // Assign new survey (POST) - requires client + client_code
        const resolvedClientForAssignment = resolvedClient;
        
        // Get client ID - prioritize resolved client, then try to find from clients array
        let clientId = resolvedClientForAssignment?.id;
        if (!clientId && clients.length > 0) {
          // If no resolved client ID, use the first client from the array
          clientId = clients[0].id;
        }
        
        const clientCode = getClientCode(); // Use the helper function that has all fallbacks

        if (!clientId) {
          setError(`Client information is required to assign survey. Resolved client: ${resolvedClientForAssignment ? 'Found' : 'Not found'}, Property client_id: ${property?.client_id || 'Missing'}, Property bank_name: ${property?.bank_name || 'Missing'}`);
          return;
        }
        if (!clientCode || clientCode === 'Not available') {
          setError(`Client code is required to generate survey number. Resolved client: ${resolvedClientForAssignment ? 'Found' : 'Not found'}, Client code: ${clientCode || 'Missing'}`);
          return;
        }

        const assignBody = {
          client: parseInt(clientId), // Ensure client ID is a number
          client_code: clientCode,
          property: parseInt(property?.id), // Ensure property ID is a number
          surveyor_id: parseInt(selectedSurveyor)
        };
        
        // Check if client ID exists in clients array
        const clientExists = clients.some(c => c.id === parseInt(clientId));
        if (!clientExists) {
          setError(`Client ID ${clientId} does not exist in available clients. Available clients: ${clients.map(c => c.id).join(', ')}`);
          return;
        }
        
        // Check if surveyor ID exists in surveyors array
        const surveyorExists = surveyors.some(s => s.value === parseInt(selectedSurveyor));
        if (!surveyorExists) {
          setError(`Surveyor ID ${selectedSurveyor} does not exist in available surveyors. Available surveyors: ${surveyors.map(s => s.value).join(', ')}`);
          return;
        }
        
        response = await axios.post(API_ENDPOINTS.SURVEY_ASSIGN, assignBody, { headers });
        successMsg = response.data?.message || 'Survey assigned successfully';
      }

      setSuccessMessage(successMsg);
      
      // Call onSuccess to update parent state (but don't show success there)
      onSuccess(successMsg, response.data?.data);
      
      // Auto close modal after 2 seconds
      setTimeout(() => {
        setSuccessMessage('');
        onClose();
      }, 2000);
      
    } catch (error) {
      // Handle different types of errors
      let errorMessage = '';
      
      if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error?.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error?.response?.status) {
        // Handle specific HTTP status codes
        switch (error.response.status) {
          case 400:
            errorMessage = 'Invalid request. Please check your data and try again.';
            break;
          case 401:
            errorMessage = 'Authentication required. Please log in again.';
            break;
          case 403:
            errorMessage = 'You do not have permission to perform this action.';
            break;
          case 404:
            errorMessage = 'Survey not found. Please refresh and try again.';
            break;
          case 500:
            errorMessage = 'Server error. Please try again later.';
            break;
          default:
            errorMessage = `Failed to ${existingSurveyInfo ? 'reassign' : 'assign'} survey. Status: ${error.response.status}. Please try again.`;
        }
      } else if (error?.message) {
        errorMessage = error.message;
      } else {
        errorMessage = 'An unexpected error occurred. Please try again.';
      }
      
      setError(errorMessage);
    } finally {
      setIsAssigning(false);
    }
  };

  const handleClose = () => {
    setSelectedSurveyor('');
    setSurveyorSearch('');
    setError('');
    setSuccessMessage('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-card rounded-lg border border-border w-full max-w-md shadow-lg max-h-[90vh] flex flex-col">
        <div className="p-4 sm:p-6 overflow-y-auto flex-1">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
              <Icon name="Calendar" size={20} className="sm:w-6 sm:h-6" color="var(--color-blue-600)" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-base sm:text-lg font-semibold text-foreground">
                {existingSurveyInfo ? 'Reassign Survey' : 'Assign Survey'}
              </h3>
              <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                {existingSurveyInfo ? 'Reassign this survey to a different surveyor' : 'Assign a surveyor to this property'}
              </p>
            </div>
          </div>
          
          <div className="mb-6">
            <div className={`mb-4 p-3 rounded-md ${resolvedClient ? 'bg-muted' : 'bg-red-50 border border-red-200'}`}>
              <h4 className="text-xs sm:text-sm font-medium text-foreground mb-2">Property Details</h4>
              <div className="space-y-1.5 text-xs sm:text-sm">
                <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-2">
                  <span className="text-muted-foreground font-medium sm:font-normal">Address:</span>
                  <span className="text-foreground break-words sm:text-right">{property?.property_address || 'N/A'}</span>
                </div>
                <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-2">
                  <span className="text-muted-foreground font-medium sm:font-normal">Customer:</span>
                  <span className="text-foreground break-words sm:text-right">{property?.customer_name || 'N/A'}</span>
                </div>
                <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-2">
                  <span className="text-muted-foreground font-medium sm:font-normal">Client:</span>
                  <span className={`break-words sm:text-right ${getClientName() !== 'Not found' ? 'text-foreground' : 'text-red-600'}`}>
                    {getClientName()}
                  </span>
                </div>
                <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-2">
                  <span className="text-muted-foreground font-medium sm:font-normal">Client Code:</span>
                  <span className={`break-words sm:text-right ${getClientCode() !== 'Not available' ? 'text-foreground' : 'text-red-600'}`}>
                    {getClientCode()}
                  </span>
                </div>
              </div>
              {!resolvedClient && (
                <div className="mt-2 p-2 bg-red-100 rounded text-xs text-red-700">
                  ⚠️ Client information is required for survey assignment. Please ensure the property is properly linked to a client.
                </div>
              )}
            </div>

            {/* Existing Survey Warning */}
            {existingSurveyInfo && (
              <div className="mb-4 p-3 rounded-md bg-blue-50 border border-blue-200">
                <div className="flex items-start gap-2">
                  <Icon name="Info" size={16} color="var(--color-blue-600)" className="flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs sm:text-sm font-medium text-blue-800 break-words">
                      Currently assigned to: {existingSurveyInfo.surveyor?.username}
                    </p>
                    <p className="text-xs text-blue-700 mt-1 break-words">
                      Survey Number: {existingSurveyInfo.surveyNumber} | Status: {existingSurveyInfo.status}
                    </p>
                    <p className="text-xs text-blue-600 mt-1 font-medium">
                      Select a new surveyor to reassign this survey.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="mb-4">
              <label className="block text-xs sm:text-sm font-medium text-foreground mb-2">
                {existingSurveyInfo ? 'Select New Surveyor *' : 'Select Surveyor *'}
              </label>
              {isLoading ? (
                <div className="flex items-center justify-center p-3 sm:p-4 border border-border rounded-md">
                  <Icon name="Loader2" size={18} className="sm:w-5 sm:h-5 animate-spin mr-2" />
                  <span className="text-xs sm:text-sm text-muted-foreground">Loading surveyors...</span>
                </div>
              ) : surveyors.length === 0 ? (
                <div className="flex items-center justify-center p-3 sm:p-4 border border-border rounded-md bg-gray-50">
                  <div className="text-center">
                    <Icon name="Users" size={18} className="sm:w-5 sm:h-5 mx-auto mb-2 text-gray-400" />
                    <p className="text-xs sm:text-sm text-gray-600">
                      No surveyors available
                    </p>
                  </div>
                </div>
              ) : (
                <div className="border border-border rounded-md overflow-hidden">
                  {/* Search box */}
                  <div className="p-2 border-b border-border bg-muted/20">
                    <input
                      type="text"
                      placeholder="Search surveyor..."
                      value={surveyorSearch}
                      onChange={e => setSurveyorSearch(e.target.value)}
                      className="w-full text-xs sm:text-sm border border-border rounded px-2 py-1.5 bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                    />
                  </div>
                  <div className="max-h-52 overflow-y-auto divide-y divide-border">
                    {surveyors
                      .filter(s => !surveyorSearch || s.label?.toLowerCase().includes(surveyorSearch.toLowerCase()))
                      .map(s => {
                        const sid = s.value;
                        const st = surveyorStats[sid] || { pending: 0, completed: 0, total: 0 };
                        const isHighLoad = st.pending >= 5;
                        const isSelected = String(selectedSurveyor) === String(sid);
                        return (
                          <div
                            key={sid}
                            onClick={() => setSelectedSurveyor(sid)}
                            className={`px-3 py-2.5 cursor-pointer transition-colors ${
                              isSelected
                                ? 'bg-primary text-primary-foreground'
                                : 'hover:bg-muted'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-2">
                                <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                                  isSelected ? 'bg-primary-foreground/20' : 'bg-primary/10'
                                }`}>
                                  <Icon name="User" size={12} color={isSelected ? 'currentColor' : 'var(--color-primary)'} />
                                </div>
                                <span className="text-xs sm:text-sm font-medium">{s.label}</span>
                              </div>
                              {isHighLoad && !isSelected && (
                                <span className="text-xs px-1.5 py-0.5 rounded bg-orange-100 text-orange-700 font-medium">High Load</span>
                              )}
                              {isSelected && (
                                <Icon name="Check" size={14} />
                              )}
                            </div>
                            {/* Workload stats */}
                            <div className="flex items-center space-x-3 mt-1.5 ml-8">
                              <span className={`text-xs flex items-center space-x-1 ${
                                isSelected ? 'text-primary-foreground/80' : 'text-yellow-600'
                              }`}>
                                <Icon name="Clock" size={10} />
                                <span>{st.pending} pending</span>
                              </span>
                              <span className={`text-xs flex items-center space-x-1 ${
                                isSelected ? 'text-primary-foreground/80' : 'text-green-600'
                              }`}>
                                <Icon name="CheckCircle" size={10} />
                                <span>{st.completed} completed</span>
                              </span>
                              <span className={`text-xs flex items-center space-x-1 ${
                                isSelected ? 'text-primary-foreground/80' : 'text-muted-foreground'
                              }`}>
                                <Icon name="BarChart2" size={10} />
                                <span>{st.total} total</span>
                              </span>
                            </div>
                          </div>
                        );
                      })
                    }
                  </div>
                </div>
              )}
            </div>

            {error && (
              <div className="mb-4 p-3 rounded-md bg-red-50 border border-red-200">
                <div className="flex items-start gap-2">
                  <Icon name="AlertCircle" size={16} color="var(--color-red-600)" className="flex-shrink-0 mt-0.5" />
                  <p className="text-xs sm:text-sm text-red-700 break-words flex-1">{error}</p>
                </div>
              </div>
            )}

            {successMessage && (
              <div className="mb-4 p-3 rounded-md bg-green-50 border border-green-200">
                <div className="flex items-start gap-2">
                  <Icon name="CheckCircle" size={16} color="var(--color-green-600)" className="flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs sm:text-sm text-green-700 font-medium break-words">{successMessage}</p>
                    <p className="text-xs text-green-600 mt-1">This modal will close automatically in 2 seconds...</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-end gap-2 sm:gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isAssigning}
              className="w-full sm:w-auto"
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="default"
              onClick={handleAssignSurvey}
              disabled={isAssigning || !selectedSurveyor || !resolvedClient || !!successMessage}
              loading={isAssigning}
              iconName={successMessage ? "CheckCircle" : (existingSurveyInfo ? "RefreshCw" : "Calendar")}
              iconPosition="left"
              className="w-full sm:w-auto"
            >
              {successMessage 
                ? 'Success!' 
                : (isAssigning 
                    ? (existingSurveyInfo ? 'Reassigning...' : 'Assigning...')
                    : (existingSurveyInfo ? 'Reassign Survey' : 'Assign Survey'))}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SurveyAssignmentModal;
